import { useMemo } from 'react';
import { useController } from 'react-hook-form';
import {
  isValidDateOfBirth,
  isValidEmailAddress,
  isValidMinimumAge,
} from 'utils/inputProcessingUtils/inputProcessingUtils';
import { ProfileType, SignupFormData } from 'features/signUp/types';
import { MINIMUM_USER_AGE } from 'features/signUp/constants';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { useSignupData } from 'features/signUp/hooks/useSignupData';
import { isValidPhoneNumber } from 'utils/phoneNumberUtils/phoneNumberUtils';
import { useCobrand } from 'features/cobrand/hooks/useCobrandUtils';
import stateOfResidenceDropdownData from './form-dropdown-data/state_of_residence_dropdown_data.json';
import territoriesDropdownData from './form-dropdown-data/territories_dropdown_data.json';
import genderDropdownData from './form-dropdown-data/gender_dropdown_data.json';
import { resolveOptInMessage } from './resolveOptInMessage';

export const useProfileCreationStep = (
  profileType: ProfileType,
  requiredInputErrorMessage: string,
  invalidPhoneNumberErrorMessage: string,
) => {
  const {
    isEligibilityCheckRequired,
    isUserInvitedCaregiver,
    isHospitalSsoEnabled,
    notificationFlag,
    isHospitalSmsEnabled,
    optInMessage,
    privacyPolicy,
    eulaPolicy,
  } = useSignupData();
  const { cobrand } = useCobrand();
  const { translation, currentLocale } = useTranslationContext();
  const emptyFieldErrorMessage = translation('empty_field_error');
  const invalidMemberEmailErrorMessage = translation('invalid_email_error');
  const titleTranslationKey =
    profileType === 'memberByCaregiverProxy'
      ? 'profile_caregivers_member_header'
      : 'profile_header';
  const subtitleTranslationKey = useMemo(() => {
    switch (profileType) {
      case 'member':
        return 'profile_member_body';
      case 'memberByCaregiverProxy':
        return null;
      case 'caregiver':
        return 'profile_caregiver_body';
      default:
        return null;
    }
  }, [profileType]);

  const showRelationshipToMemberDropdown = profileType === 'caregiver';
  const showGenderDropdown = profileType !== 'caregiver';
  const showPhoneNumberField = profileType !== 'memberByCaregiverProxy';

  const firstNameField = profileType === 'caregiver' ? 'caregiverFirstName' : 'memberFirstName';
  const lastNameField = profileType === 'caregiver' ? 'caregiverLastName' : 'memberLastName';
  const dateOfBirthField =
    profileType === 'caregiver' ? 'caregiverDateOfBirth' : 'memberDateOfBirth';
  const stateOfResidenceField =
    profileType === 'caregiver' ? 'caregiverStateOfResidence' : 'memberStateOfResidence';
  const phoneNumberField =
    profileType === 'caregiver' ? 'caregiverPhoneNumber' : 'memberPhoneNumber';
  const showEmailField =
    profileType === 'member' && isHospitalSsoEnabled && !isUserInvitedCaregiver;

  const firstName = useController({
    name: firstNameField,
    rules: {
      required: requiredInputErrorMessage,
      validate: (value) =>
        (typeof value === 'string' && !!value.trim()) || requiredInputErrorMessage,
    },
  });

  const lastName = useController<SignupFormData>({
    name: lastNameField,
    rules: {
      required: requiredInputErrorMessage,
      validate: (value) =>
        (typeof value === 'string' && !!value.trim()) || requiredInputErrorMessage,
    },
  });

  const dateOfBirth = useController<SignupFormData>({
    name: dateOfBirthField,
    rules: {
      required: requiredInputErrorMessage,
      validate: (value) => {
        if (typeof value === 'string') {
          if (!isValidDateOfBirth(value)) {
            return translation('wrong_date_of_birth');
          }
        }
        // Self User flow - For member profiles, validate minimum age
        // Caregiver flow - For caregiver profiles, we validate caregiver's minimum age not member's age
        if (typeof value === 'string' && profileType !== 'memberByCaregiverProxy') {
          if (!isValidMinimumAge(value, MINIMUM_USER_AGE)) {
            return translation('dob_field_underage_error');
          }
        }

        return true;
      },
    },
  });

  const caregiverRelationshipToMember = useController<SignupFormData>({
    name: 'caregiverRelationshipToMember',
    rules: {
      required: showRelationshipToMemberDropdown ? requiredInputErrorMessage : false,
    },
  });

  const gender = useController<SignupFormData>({
    name: 'memberGender',
    rules: {
      required: showGenderDropdown ? requiredInputErrorMessage : false,
    },
  });

  const stateOfResidence = useController<SignupFormData>({
    name: stateOfResidenceField,
    rules: {
      required: requiredInputErrorMessage,
    },
  });

  const phoneNumber = useController<SignupFormData>({
    name: phoneNumberField,
    rules: {
      required: showPhoneNumberField ? requiredInputErrorMessage : false,
      // Becuase the validate function is executed on component mount,
      // when the field is not required we want to always return true
      validate: (input?: string | boolean) => {
        if (!showPhoneNumberField) {
          return true;
        }

        return (
          (typeof input === 'string' && isValidPhoneNumber(input)) || invalidPhoneNumberErrorMessage
        );
      },
    },
  });

  const memberProfileEmail = useController<SignupFormData>({
    name: 'memberProfileEmail',
    rules: {
      required: showEmailField ? emptyFieldErrorMessage : false,
      validate: (input: string | boolean | undefined) => {
        if (!showEmailField) return true;

        return (
          (typeof input === 'string' && isValidEmailAddress(input)) ||
          invalidMemberEmailErrorMessage
        );
      },
    },
  });

  const smsOptIn = useController<SignupFormData>({
    name: 'smsOptIn',
    defaultValue: false,
  });
  const isNotificationFlagEnabled = useMemo(
    () => notificationFlag?.isEnabled ?? false,
    [notificationFlag],
  );

  // Checkbox is shown when EITHER the hospital SMS field is on OR the server-side flag is on.
  const showSmsOptInCheckbox = Boolean(isHospitalSmsEnabled) || isNotificationFlagEnabled;

  const smsOptInLabel = useMemo(() => {
    // Custom message only when the hospital SMS field is enabled; otherwise default.
    if (!isHospitalSmsEnabled) {
      return translation('sms_opt_in_checkbox_label');
    }

    return (
      resolveOptInMessage({
        message: optInMessage,
        locale: currentLocale,
        applicationName: cobrand.displayName,
        privacyPolicyUrl: privacyPolicy?.url,
        termsOfServiceUrl: eulaPolicy?.url,
        privacyPolicyLabel: translation('privacy_policy'),
        termsOfServiceLabel: 'Terms of Service', // TODO - Translations WFMOB-16756
      }) ?? translation('sms_opt_in_checkbox_label')
    );
  }, [
    isHospitalSmsEnabled,
    optInMessage,
    currentLocale,
    translation,
    cobrand,
    privacyPolicy,
    eulaPolicy,
  ]);
  return {
    titleTranslationKey,
    subtitleTranslationKey,
    showRelationshipToMemberDropdown,
    showGenderDropdown,
    stateOfResidenceDropdownData,
    territoriesDropdownData,
    genderDropdownData,
    showPhoneNumberField,
    isDOBFieldDisabled:
      (profileType !== 'caregiver' && isEligibilityCheckRequired) ||
      (profileType === 'member' && isHospitalSsoEnabled),
    isNameFieldDisabled: profileType === 'caregiver' && isUserInvitedCaregiver,
    inputController: {
      firstName,
      lastName,
      dateOfBirth,
      caregiverRelationshipToMember,
      gender,
      stateOfResidence,
      phoneNumber,
      smsOptIn,
      memberProfileEmail,
    },
    isNotificationFlagEnabled,
    showEmailField,
    smsOptInLabel,
    showSmsOptInCheckbox,
    isHospitalSmsEnabled,
  };
};
